import React from "react";
import PropTypes from "prop-types";
import { useI18next } from "gatsby-plugin-react-i18next";
import data from "../../data/restaurants.json";
import LinkGrid from "../LinkCard/LinkGrid.jsx";
import LinkCard from "../LinkCard/LinkCard.jsx";
import TagFilterMenu from "../LinkCard/TagFilterMenu.jsx";
import useTagFiltering from "../LinkCard/useTagFiltering.js";
import "./Restaurants.scss";

const Restaurants = ({
  limit,
  columns = 3,
  showTags = true,
  withFilterMenu = false,
}) => {
  const { language, defaultLanguage, t } = useI18next?.() || {};
  const lang = language || defaultLanguage || "fr";
  const fallbackLang = defaultLanguage || "fr";

  const {
    items,
    allTags,
    activeTag,
    setSelectedTag: setActiveTag,
    allLabel,
    tagCounts,
    totalCount,
  } = useTagFiltering({
    data,
    lang,
    fallbackLang,
    limit,
    translate: t,
    allLabelKey: "__Tous",
  });

  // Map known tags to emojis for display in filter chips
  const tagEmoji = React.useCallback((rawTag) => {
    if (!rawTag) return "";
    const raw = String(rawTag).trim();
    // Coup de cœur / Favourite / Colpo di fulmine
    if (
      /coup\s+de\s+c(?:œur|oeur)/i.test(raw) ||
      /favou?rite/i.test(raw) ||
      /colpo\s+di\s+fulmine/i.test(raw) ||
      /colpu\s+di\s+core/i.test(raw)
    ) {
      return "❤️";
    }
    // Proche & à pied / Close & walkable / Vicino, a piedi
    if (
      /proche[\s\S]*pied/i.test(raw) ||
      /close[\s\S]*walk/i.test(raw) ||
      /vicin[oa][\s\S]*piedi/i.test(raw) ||
      /\bpedi\b/i.test(raw)
    ) {
      return "🚶";
    }
    // Street‑food / food truck (various hyphens)
    const streetFoodNormalized = raw
      .toLowerCase()
      .replaceAll(/[-\u2010-\u2015\u2212]/g, " ")
      .replaceAll(/\s+/g, " ");
    if (
      streetFoodNormalized.includes("street food") ||
      streetFoodNormalized.includes("food truck")
    ) {
      return "🍔";
    }
    // Pizzeria
    if (/\bpizzeria\b/i.test(raw)) return "🍕";
    // Bord de mer / plage / vue mer / seafront / sea view / fronte mare / vista mare / spiaggia
    const seasideNormalized = raw
      .toLowerCase()
      .replaceAll(/[-\u2010-\u2015\u2212]/g, " ")
      .replaceAll(/\s+/g, " ")
      .trim();
    if (
      seasideNormalized.includes("bord de mer") ||
      seasideNormalized.includes("plage") ||
      seasideNormalized.includes("vue mer") ||
      seasideNormalized.includes("seafront") ||
      seasideNormalized.includes("sea view") ||
      seasideNormalized.includes("beach") ||
      seasideNormalized.includes("fronte mare") ||
      seasideNormalized.includes("vista mare") ||
      seasideNormalized.includes("spiaggia")
    ) {
      return "🏖️";
    }
    // Italien(ne) / Italian(o/a)
    if (
      /\bitalien(ne)?\b/i.test(raw) ||
      /\bitalian[oa]?\b/i.test(raw) ||
      /\btaliana\b/i.test(raw)
    )
      return "🇮🇹";
    // Café / Bar / Coffee / Caffè  (prioritize wine bars)
    if (
      /bar\s*[aà]\s*vins?/i.test(raw) ||
      /wine\s*bar/i.test(raw) ||
      /\benoteca\b/i.test(raw)
    ) {
      return "🍷";
    }
    if (/(café|cafe|coffee|caff[èe])/i.test(raw)) return "☕";
    if (/\bbar\b/i.test(raw)) return "🍹";
    return "";
  }, []);

  if (!items.length) return null;

  return (
    <>
      {withFilterMenu && allTags.length > 0 && (
        <TagFilterMenu
          allTags={allTags}
          activeTag={activeTag}
          setActiveTag={setActiveTag}
          allLabel={allLabel}
          ariaLabel={t?.("__Filtrer par tags") || "Filtrer par tags"}
          tagEmoji={tagEmoji}
          tagCounts={tagCounts}
          totalCount={totalCount}
        />
      )}
      <LinkGrid columns={columns}>
        {items.map((r, idx) => {
          const key = `${r.name || ""}|${r.website || r.mapsUrl || idx}`;
          return (
            <LinkCard
              key={key}
              title={r.name}
              href={r.website || r.mapsUrl || "#"}
              website={r.website}
              image={r.image}
              description={r.description}
              secondaryHref={r.mapsUrl}
              tag={showTags ? r.tag : undefined}
              tags={showTags ? r.tags : undefined}
              phone={r.phone}
            />
          );
        })}
      </LinkGrid>
    </>
  );
};

Restaurants.propTypes = {
  limit: PropTypes.number,
  columns: PropTypes.number,
  showTags: PropTypes.bool,
  withFilterMenu: PropTypes.bool,
};

export default Restaurants;
